/**
 * Rektifikatsiya kolonnasi — turg'un rejim (McCabe-Thiele).
 *
 * ALGORITM
 *   1. VLE (Raoult yoki o'zgarmas α), oziqlanish termik holati q.
 *   2. Rejim yechuvchisi (modes.ts): xD, xW, D, W, bosqichlar, oziqlanish bosqichi.
 *   3. Haroratlar (qaynash nuqtalari), Fenske Nmin, issiqlik yuklamalari (energy.ts),
 *      gidravlika ikki seksiyada (hydraulics.ts), diagramma.
 *
 * MODDIY BALANS: F = D + W,  F·xF = D·xD + W·xW  (mol/s, yengil komponent mol ulushi).
 *
 * "YECHIM YO'Q" HOLATLARI (xato tashlanmaydi):
 *   R ≤ Rmin             → column.belowMinimumReflux, N = stagesExact = Infinity, converged = false
 *   Nmin ≥ MAX_STAGES    → column.unattainablePurity, N = Infinity, converged = false
 *   xD ≤ xF yoki xW ≥ xF → column.specInfeasible, N = NaN, converged = false
 *   rating, xD berilgan, R ≤ Rmin(xD) → belowMinimumReflux, D = 0, W = F, xW = xF
 *
 * OGOHLANTIRISH KODLARI (i18n):
 *   column.nonIdealMixture        info      Raoult noideal juftlik uchun ishlatilmoqda
 *   column.noFeed                 critical  F = 0
 *   column.specInfeasible         critical  xD ≤ xF yoki xW ≥ xF (yoki D/F chegaradan tashqarida)
 *   column.belowMinimumReflux     critical  R ≤ Rmin — pinch, cheksiz bosqichlar
 *   column.unattainablePurity     warning   berilgan α (yoki N) bilan xD/xW ga erishib bo'lmaydi
 *   column.overdesigned           info      rating: bosqichlar ortiqcha, mahsulot tozalik chegarasida
 *   column.feedStageSuboptimal    info      rating: oziqlanish bosqichi optimal joyda emas
 *   column.refluxFarAboveMinimum  info      R/Rmin > 3
 *   column.insufficientBoilup     critical  chiqarish seksiyasida bug' oqimi V' ≤ 0
 *   column.flooding               critical  u/u_flood > 0.8
 *   column.weeping                warning   u/u_flood < 0.4
 *   property.*                    (properties/ dan) xossa oralig'idan chiqish
 */
import { warn, type SteadyResult, type Warning } from '../../types';
import { feedQualityFromTemperature, heatDuties, mixtureProps } from './energy';
import { sectionHydraulics } from './hydraulics';
import { fenskeMinimumStages } from './mccabe-thiele';
import { meanAlpha, solveDesign, solveRating } from './modes';
import type {
  McCabeThieleDiagram,
  RectificationInputs,
  RectificationOutputs,
  RectificationParams,
  SectionHydraulics,
} from './types';
import { validateInputs, validateParams } from './validate';
import { equilibriumCurve, isKnownNonIdealPair, makeVle } from './vle';

const round3 = (v: number): number => Number(v.toFixed(3));

export function solveSteady(
  p: RectificationParams,
  inputs: RectificationInputs,
): SteadyResult<RectificationOutputs> {
  validateParams(p);
  validateInputs(p, inputs);
  const warnings: Warning[] = [];
  const vle = makeVle(p.lightComponent, p.heavyComponent, p.pressure, p.relativeVolatility);
  if (vle.mode === 'raoult' && isKnownNonIdealPair(p.lightComponent, p.heavyComponent))
    warn(warnings, 'column.nonIdealMixture', 'info', {
      light: p.lightComponent,
      heavy: p.heavyComponent,
    });
  const mix = mixtureProps(p.lightComponent, p.heavyComponent);
  const F = inputs.feedFlow;
  const { xF, refluxRatio: R } = inputs;
  const E = p.trayEfficiency ?? 1;

  // Oziqlanish termik holati
  let q = 1;
  const Tfeed = vle.bubbleT(xF);
  if (inputs.feedQuality !== undefined) q = inputs.feedQuality;
  else if (inputs.feedTemperature !== undefined)
    q = feedQualityFromTemperature(vle, mix, xF, inputs.feedTemperature, warnings).q;

  if (F <= 0) warn(warnings, 'column.noFeed', 'critical');

  const s =
    p.mode === 'design'
      ? solveDesign(vle, inputs, q, warnings)
      : solveRating(vle, p, inputs, q, warnings);
  const { xD, xW, D, W } = s;
  const stagesExact = s.counted.stagesExact;
  // Kasr bosqichda 1e-6 dan kichik "ortiqcha" — sonli shovqin (masalan R → ∞ da 6.000001 → 6)
  const N_theoretical = Number.isFinite(stagesExact)
    ? Math.max(1, Math.ceil(stagesExact - 1e-6))
    : stagesExact;
  const N_actual = Number.isFinite(N_theoretical)
    ? Math.ceil((N_theoretical - 1) / E)
    : N_theoretical;
  const feedStage = s.counted.stepping ? s.counted.stepping.feedStage : (p.feedStage ?? 0);

  const alpha = meanAlpha(vle, xD, xW);
  const Nmin = fenskeMinimumStages(alpha, xD, xW);
  const Rmin = s.counted.Rmin;
  if (Number.isFinite(Rmin) && Rmin > 0 && R / Rmin > 3 && s.converged)
    warn(warnings, 'column.refluxFarAboveMinimum', 'info', { ratio: round3(R / Rmin) });

  // Haroratlar va issiqlik
  const Ttop = vle.bubbleT(xD);
  const Tbottom = vle.bubbleT(xW);
  const duties = heatDuties(mix, { F, D, W, R, q, xF, xD, xW, Ttop, Tbottom, Tfeed }, warnings);

  // Gidravlika: tepa (V, L) va tub (V', L') seksiyalari
  const V = (R + 1) * D;
  const L = R * D;
  const Vs = V - (1 - q) * F;
  const Ls = L + q * F;
  if (F > 0 && Vs <= 0)
    warn(warnings, 'column.insufficientBoilup', 'critical', { Vstrip: round3(Vs) });
  const top = sectionHydraulics(p, { x: xD, y: xD, T: Ttop, V, L }, warnings);
  const bottom = sectionHydraulics(
    p,
    { x: xW, y: vle.y(xW), T: Tbottom, V: Math.max(Vs, 0), L: Ls },
    warnings,
  );
  const worst: SectionHydraulics = top.floodingFraction >= bottom.floodingFraction ? top : bottom;
  if (F > 0 && D > 0) {
    if (worst.floodingFraction > 0.8)
      warn(warnings, 'column.flooding', 'critical', { fraction: round3(worst.floodingFraction) });
    else if (worst.floodingFraction < 0.4)
      warn(warnings, 'column.weeping', 'warning', { fraction: round3(worst.floodingFraction) });
  }

  const lines = s.counted.lines;
  const diagram: McCabeThieleDiagram = {
    equilibrium: equilibriumCurve(vle, 50),
    rectifyingLine: [
      { x: lines.xInt, y: lines.yInt },
      { x: xD, y: xD },
    ],
    strippingLine: [
      { x: xW, y: xW },
      { x: lines.xInt, y: lines.yInt },
    ],
    qLine: [
      { x: xF, y: xF },
      { x: lines.xInt, y: lines.yInt },
    ],
    stairs: s.counted.stepping ? s.counted.stepping.stairs : [],
  };

  const diagnostics =
    s.message !== undefined
      ? {
          converged: s.converged,
          iterations: s.iterations,
          residual: s.residual,
          message: s.message,
        }
      : { converged: s.converged, iterations: s.iterations, residual: s.residual };

  return {
    outputs: {
      N_theoretical,
      stagesExact,
      N_actual,
      feedStage,
      Rmin,
      Nmin,
      R,
      q,
      alpha,
      D,
      W,
      xD,
      xW,
      Qcondenser: duties.Qcondenser,
      Qreboiler: duties.Qreboiler,
      Ttop,
      Tbottom,
      Tfeed,
      vaporVelocity: worst.vaporVelocity,
      floodingVelocity: worst.floodingVelocity,
      floodingFraction: worst.floodingFraction,
      hydraulics: { top, bottom },
      diagram,
    },
    warnings,
    diagnostics,
  };
}
